import type { FeedMessage } from "@/types";
import type { FeedCursor, FeedPage } from "@/api/feed";
import { compareFeedCursor } from "@/utils/messageId";

/** Пока на 217 нет proxy /api/feed — читаем /data/messages_feed.jsonl с CDN. */
export async function loadStaticFeedArchive(): Promise<FeedMessage[]> {
  const res = await fetch("/data/messages_feed.jsonl");
  if (!res.ok) {
    throw new Error(
      `HTTP ${res.status} — нет /data/messages_feed.jsonl на фронте`
    );
  }
  const parsed: FeedMessage[] = [];
  for (const line of (await res.text()).trim().split("\n")) {
    if (!line.trim()) continue;
    parsed.push(JSON.parse(line) as FeedMessage);
  }
  parsed.sort((a, b) => compareFeedCursor(a, b));
  return parsed;
}

export function sliceFeedPage(
  archive: FeedMessage[],
  limit: number,
  cursor?: FeedCursor
): FeedPage {
  const pool =
    cursor == null
      ? archive
      : archive.filter(
          (m) =>
            compareFeedCursor(m, {
              date: cursor.beforeDate,
              id: cursor.beforeId,
            }) < 0
        );
  const has_more = pool.length > limit;
  const messages = pool.slice(-limit);
  return { messages, has_more, has_more_newer: false, count: archive.length };
}

export function sliceFeedPageAfter(
  archive: FeedMessage[],
  limit: number,
  cursor: { afterId: string; afterDate: string }
): FeedPage {
  const pool = archive.filter(
    (m) =>
      compareFeedCursor(m, { date: cursor.afterDate, id: cursor.afterId }) > 0
  );
  const has_more_newer = pool.length > limit;
  const messages = pool.slice(0, limit);
  return {
    messages,
    has_more: false,
    has_more_newer,
    count: archive.length,
  };
}

export function sliceFeedAround(
  archive: FeedMessage[],
  messageId: string,
  before: number,
  after: number
): FeedPage {
  const key = String(messageId);
  const idx = archive.findIndex((m) => String(m.id) === key);
  if (idx < 0) {
    return {
      messages: [],
      has_more: false,
      has_more_newer: false,
      count: archive.length,
    };
  }
  const start = Math.max(0, idx - before);
  const end = Math.min(archive.length, idx + after + 1);
  return {
    messages: archive.slice(start, end),
    has_more: start > 0,
    has_more_newer: end < archive.length,
    count: archive.length,
  };
}

export function isFeedApiUnavailable(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  // Пустая лента API — не повод подставлять демо-архив с /data/messages_feed.jsonl
  if (/Feed DB empty/i.test(err.message)) return false;
  return /HTTP (404|502|503)/i.test(err.message);
}
